import { Component, Input, OnInit, OnDestroy, ElementRef, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ContractService, ContractDetails, TemplateFillRequest } from './contract.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/**
 * Split-Screen Contract Preview Component
 * 
 * Displays contract metadata on the left and PDF preview on the right.
 * Can be used for:
 * - Viewing existing contracts
 * - Previewing new contracts before saving
 */
@Component({
  selector: 'app-contract-preview',
  template: `
    <div class="contract-preview-container">
      <!-- Loading State -->
      <div class="loading-overlay" *ngIf="isLoading">
        <mat-spinner diameter="40"></mat-spinner>
        <p>{{ loadingMessage }}</p>
      </div>

      <!-- Error State -->
      <div class="error-message" *ngIf="errorMessage">
        <mat-icon>error</mat-icon>
        <p>{{ errorMessage }}</p>
        <button mat-button color="primary" (click)="retry()">Retry</button>
      </div>

      <!-- Split Screen Content -->
      <div class="split-screen" *ngIf="!isLoading && !errorMessage">
        
        <!-- Left Panel: Metadata -->
        <div class="metadata-panel" [class.collapsed]="metadataCollapsed">
          <div class="panel-header">
            <h3>
              <mat-icon>info</mat-icon>
              Contract Details
            </h3>
            <button mat-icon-button (click)="metadataCollapsed = !metadataCollapsed">
              <mat-icon>{{ metadataCollapsed ? 'chevron_right' : 'chevron_left' }}</mat-icon>
            </button>
          </div>

          <div class="panel-content" *ngIf="!metadataCollapsed">
            <!-- Contract Info Section -->
            <mat-expansion-panel expanded>
              <mat-expansion-panel-header>
                <mat-panel-title>
                  <mat-icon>description</mat-icon>
                  Contract Information
                </mat-panel-title>
              </mat-expansion-panel-header>
              
              <div class="metadata-grid">
                <div class="metadata-item" *ngIf="contract?.contractNumber">
                  <label>Contract Number</label>
                  <span class="value highlight">{{ contract.contractNumber }}</span>
                </div>
                <div class="metadata-item" *ngIf="contract?.contractTitle">
                  <label>Title</label>
                  <span class="value">{{ contract.contractTitle }}</span>
                </div>
                <div class="metadata-item" *ngIf="contract?.contractType">
                  <label>Type</label>
                  <span class="value">{{ contract.contractType }}</span>
                </div>
                <div class="metadata-item" *ngIf="contract?.contractStatus">
                  <label>Status</label>
                  <span class="status-badge" [ngClass]="getStatusClass(contract.contractStatus)">
                    {{ contract.contractStatus }}
                  </span>
                </div>
                <div class="metadata-item" *ngIf="contract?.priority">
                  <label>Priority</label>
                  <span class="priority-badge" [ngClass]="getPriorityClass(contract.priority)">
                    {{ contract.priority }}
                  </span>
                </div>
              </div>
            </mat-expansion-panel>

            <!-- Parties Section -->
            <mat-expansion-panel expanded>
              <mat-expansion-panel-header>
                <mat-panel-title>
                  <mat-icon>business</mat-icon>
                  Parties
                </mat-panel-title>
              </mat-expansion-panel-header>
              
              <div class="metadata-grid">
                <div class="metadata-item" *ngIf="contract?.supplierName">
                  <label>Supplier</label>
                  <span class="value">{{ contract.supplierName }}</span>
                </div>
                <div class="metadata-item" *ngIf="contract?.departmentName">
                  <label>Department</label>
                  <span class="value">{{ contract.departmentName }}</span>
                </div>
              </div>
            </mat-expansion-panel>

            <!-- Financial Section -->
            <mat-expansion-panel expanded>
              <mat-expansion-panel-header>
                <mat-panel-title>
                  <mat-icon>payments</mat-icon>
                  Financial
                </mat-panel-title>
              </mat-expansion-panel-header>
              
              <div class="metadata-grid">
                <div class="metadata-item full-width" *ngIf="contract?.contractAmount">
                  <label>Contract Value</label>
                  <span class="value amount">
                    {{ contract.currency || 'KES' }} {{ contract.contractAmount | number:'1.2-2' }}
                  </span>
                </div>
              </div>
            </mat-expansion-panel>

            <!-- Dates Section -->
            <mat-expansion-panel expanded>
              <mat-expansion-panel-header>
                <mat-panel-title>
                  <mat-icon>calendar_today</mat-icon>
                  Key Dates
                </mat-panel-title>
              </mat-expansion-panel-header>
              
              <div class="metadata-grid">
                <div class="metadata-item" *ngIf="contract?.effectiveDate">
                  <label>Effective Date</label>
                  <span class="value">{{ contract.effectiveDate | date:'mediumDate' }}</span>
                </div>
                <div class="metadata-item" *ngIf="contract?.expiryDate">
                  <label>Expiry Date</label>
                  <span class="value" [class.expiring-soon]="isExpiringSoon()">
                    {{ contract.expiryDate | date:'mediumDate' }}
                  </span>
                </div>
                <div class="metadata-item" *ngIf="contract?.expiryDate">
                  <label>Days Remaining</label>
                  <span class="value" [ngClass]="getDaysRemainingClass()">
                    {{ calculateDaysRemaining() }} days
                  </span>
                </div>
              </div>
            </mat-expansion-panel>

            <!-- Document Info Section -->
            <mat-expansion-panel>
              <mat-expansion-panel-header>
                <mat-panel-title>
                  <mat-icon>insert_drive_file</mat-icon>
                  Document Info
                </mat-panel-title>
              </mat-expansion-panel-header>
              
              <div class="metadata-grid">
                <div class="metadata-item" *ngIf="contract?.name">
                  <label>File Name</label>
                  <span class="value">{{ contract.name }}</span>
                </div>
                <div class="metadata-item" *ngIf="contract?.sizeInBytes">
                  <label>File Size</label>
                  <span class="value">{{ formatFileSize(contract.sizeInBytes) }}</span>
                </div>
                <div class="metadata-item" *ngIf="contract?.createdBy">
                  <label>Created By</label>
                  <span class="value">{{ contract.createdBy }}</span>
                </div>
                <div class="metadata-item" *ngIf="contract?.createdAt">
                  <label>Created</label>
                  <span class="value">{{ contract.createdAt | date:'medium' }}</span>
                </div>
                <div class="metadata-item" *ngIf="contract?.modifiedBy">
                  <label>Modified By</label>
                  <span class="value">{{ contract.modifiedBy }}</span>
                </div>
                <div class="metadata-item" *ngIf="contract?.modifiedAt">
                  <label>Last Modified</label>
                  <span class="value">{{ contract.modifiedAt | date:'medium' }}</span>
                </div>
              </div>
            </mat-expansion-panel>

            <!-- Actions -->
            <div class="panel-actions">
              <button mat-raised-button color="primary" (click)="downloadDocx()">
                <mat-icon>download</mat-icon>
                Download DOCX
              </button>
              <button mat-raised-button (click)="downloadPdf()">
                <mat-icon>picture_as_pdf</mat-icon>
                Download PDF
              </button>
            </div>
          </div>
        </div>

        <!-- Right Panel: PDF Preview -->
        <div class="preview-panel">
          <div class="panel-header">
            <h3>
              <mat-icon>preview</mat-icon>
              Document Preview
            </h3>
            <div class="preview-controls">
              <button mat-icon-button (click)="zoomIn()" title="Zoom In">
                <mat-icon>zoom_in</mat-icon>
              </button>
              <button mat-icon-button (click)="zoomOut()" title="Zoom Out">
                <mat-icon>zoom_out</mat-icon>
              </button>
              <button mat-icon-button (click)="resetZoom()" title="Reset Zoom">
                <mat-icon>fit_screen</mat-icon>
              </button>
              <button mat-icon-button (click)="openInNewTab()" title="Open in New Tab">
                <mat-icon>open_in_new</mat-icon>
              </button>
            </div>
          </div>

          <div class="pdf-container" #pdfContainer>
            <iframe
              *ngIf="pdfUrl"
              [src]="pdfUrl"
              class="pdf-viewer"
              [style.transform]="'scale(' + zoomLevel + ')'"
              [style.transform-origin]="'top left'"
              frameborder="0">
            </iframe>

            <div class="no-preview" *ngIf="!pdfUrl && !isLoading">
              <mat-icon>picture_as_pdf</mat-icon>
              <p>No preview available</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .contract-preview-container {
      display: flex;
      flex-direction: column;
      height: 100%;
      min-height: 600px;
      background: #f5f7fa;
      position: relative;
    }

    .loading-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      background: rgba(255, 255, 255, 0.9);
      z-index: 100;
    }

    .loading-overlay p {
      margin-top: 16px;
      color: #666;
    }

    .error-message {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 40px;
      text-align: center;
    }

    .error-message mat-icon {
      font-size: 48px;
      width: 48px;
      height: 48px;
      color: #dc3545;
    }

    .split-screen {
      display: flex;
      flex: 1;
      gap: 16px;
      padding: 16px;
      overflow: hidden;
    }

    .metadata-panel {
      width: 400px;
      min-width: 300px;
      background: white;
      border-radius: 12px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      display: flex;
      flex-direction: column;
      overflow: hidden;
      transition: width 0.3s ease;
    }

    .metadata-panel.collapsed {
      width: 48px;
      min-width: 48px;
    }

    .preview-panel {
      flex: 1;
      background: white;
      border-radius: 12px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }

    .panel-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 16px;
      border-bottom: 1px solid #e2e8f0;
      background: #f8fafc;
    }

    .panel-header h3 {
      display: flex;
      align-items: center;
      gap: 8px;
      margin: 0;
      font-size: 16px;
      font-weight: 600;
      color: #244062;
    }

    .panel-header mat-icon {
      font-size: 20px;
      width: 20px;
      height: 20px;
    }

    .panel-content {
      flex: 1;
      overflow-y: auto;
      padding: 16px;
    }

    .metadata-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
    }

    .metadata-item {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .metadata-item.full-width {
      grid-column: 1 / -1;
    }

    .metadata-item label {
      font-size: 12px;
      color: #6c757d;
      font-weight: 500;
    }

    .metadata-item .value {
      font-size: 14px;
      color: #1a1a2e;
    }

    .metadata-item .value.highlight {
      font-weight: 600;
      color: #244062;
    }

    .metadata-item .value.amount {
      font-size: 18px;
      font-weight: 600;
      color: #28a745;
    }

    .metadata-item .value.expiring-soon {
      color: #dc3545;
    }

    .status-badge {
      display: inline-block;
      padding: 4px 12px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: 500;
    }

    .status-badge.status-draft { background: #e9ecef; color: #495057; }
    .status-badge.status-active { background: rgba(40, 167, 69, 0.1); color: #28a745; }
    .status-badge.status-expired { background: rgba(220, 53, 69, 0.1); color: #dc3545; }
    .status-badge.status-pending { background: rgba(255, 193, 7, 0.1); color: #d39e00; }

    .priority-badge {
      display: inline-block;
      padding: 4px 12px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: 500;
    }

    .priority-badge.priority-critical { background: rgba(220, 53, 69, 0.1); color: #dc3545; }
    .priority-badge.priority-high { background: rgba(255, 193, 7, 0.1); color: #d39e00; }
    .priority-badge.priority-medium { background: rgba(23, 162, 184, 0.1); color: #17a2b8; }
    .priority-badge.priority-low { background: rgba(108, 117, 125, 0.1); color: #6c757d; }

    .days-critical { color: #dc3545; font-weight: 600; }
    .days-warning { color: #ffc107; }
    .days-normal { color: #28a745; }

    .panel-actions {
      display: flex;
      gap: 12px;
      padding: 16px;
      border-top: 1px solid #e2e8f0;
      flex-wrap: wrap;
    }

    .panel-actions button {
      flex: 1;
      min-width: 120px;
    }

    .preview-controls {
      display: flex;
      gap: 4px;
    }

    .pdf-container {
      flex: 1;
      overflow: auto;
      background: #e9ecef;
      position: relative;
    }

    .pdf-viewer {
      width: 100%;
      height: 100%;
      min-height: 500px;
    }

    .no-preview {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100%;
      color: #6c757d;
    }

    .no-preview mat-icon {
      font-size: 64px;
      width: 64px;
      height: 64px;
      opacity: 0.5;
    }

    mat-expansion-panel {
      margin-bottom: 12px;
      box-shadow: none !important;
      border: 1px solid #e2e8f0;
      border-radius: 8px !important;
    }

    mat-expansion-panel-header {
      font-size: 14px;
    }

    mat-panel-title {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    mat-panel-title mat-icon {
      font-size: 18px;
      width: 18px;
      height: 18px;
      color: #244062;
    }

    @media (max-width: 992px) {
      .split-screen {
        flex-direction: column;
      }

      .metadata-panel {
        width: 100%;
        min-width: 100%;
        max-height: 300px;
      }

      .metadata-panel.collapsed {
        max-height: 48px;
      }
    }
  `]
})
export class ContractPreviewComponent implements OnInit, OnDestroy {

  @Input() contractId: string;
  @Input() previewMode = false;  // For preview before saving
  @Input() templateRequest: TemplateFillRequest;  // For preview mode

  @ViewChild('pdfContainer') pdfContainer: ElementRef;

  contract: ContractDetails;
  pdfUrl: SafeResourceUrl;
  pdfBlobUrl: string;

  isLoading = false;
  loadingMessage = 'Loading contract...';
  errorMessage: string;
  metadataCollapsed = false;
  zoomLevel = 1;

  private destroy$ = new Subject<void>();

  constructor(
    private contractService: ContractService,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    if (this.previewMode && this.templateRequest) {
      this.loadPreview();
    } else if (this.contractId) {
      this.loadContract();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    
    // Clean up blob URL
    if (this.pdfBlobUrl) {
      this.contractService.revokeBlobUrl(this.pdfBlobUrl);
    }
  }

  /**
   * Load existing contract
   */
  loadContract(): void {
    this.isLoading = true;
    this.loadingMessage = 'Loading contract...';
    this.errorMessage = null;

    this.contractService.getContract(this.contractId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (contract) => {
          this.contract = contract;
          this.loadPdfPreview();
        },
        error: (error) => {
          this.isLoading = false;
          this.errorMessage = error.message;
        }
      });
  }

  /**
   * Load PDF preview for existing contract
   */
  loadPdfPreview(): void {
    this.loadingMessage = 'Generating PDF preview...';

    this.contractService.viewContractPdf(this.contractId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob) => {
          this.pdfBlobUrl = this.contractService.createBlobUrl(blob);
          this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.pdfBlobUrl);
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          console.error('Failed to load PDF preview:', error);
          // Still show metadata even if PDF fails
        }
      });
  }

  /**
   * Load preview for new contract (before saving)
   */
  loadPreview(): void {
    this.isLoading = true;
    this.loadingMessage = 'Generating preview...';
    this.errorMessage = null;

    // Build contract details from request
    this.contract = {
      id: '',
      name: 'Preview',
      contractTitle: this.templateRequest.contractData.contractTitle,
      contractNumber: this.templateRequest.contractData.contractNumber || 'PREVIEW',
      contractType: this.templateRequest.contractData.contractType,
      contractStatus: this.templateRequest.contractData.contractStatus || 'Draft',
      supplierName: this.templateRequest.contractData.supplierName,
      departmentName: this.templateRequest.contractData.departmentName,
      contractAmount: this.templateRequest.contractData.contractAmount,
      currency: this.templateRequest.contractData.currency,
      effectiveDate: this.templateRequest.contractData.effectiveDate,
      expiryDate: this.templateRequest.contractData.expiryDate,
      priority: this.templateRequest.contractData.priority,
      createdAt: new Date().toISOString(),
      modifiedAt: new Date().toISOString(),
      createdBy: 'Preview',
      modifiedBy: 'Preview'
    };

    // Generate PDF preview
    this.contractService.previewContract(this.templateRequest, 'pdf')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob) => {
          this.pdfBlobUrl = this.contractService.createBlobUrl(blob);
          this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.pdfBlobUrl);
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          this.errorMessage = error.message;
        }
      });
  }

  retry(): void {
    if (this.previewMode) {
      this.loadPreview();
    } else {
      this.loadContract();
    }
  }

  downloadDocx(): void {
    if (this.previewMode) {
      this.contractService.previewContract(this.templateRequest, 'docx')
        .subscribe(blob => {
          this.contractService.downloadBlob(blob, `${this.contract.contractNumber}.docx`);
        });
    } else {
      this.contractService.downloadContract(this.contractId, 'docx')
        .subscribe(blob => {
          this.contractService.downloadBlob(blob, this.contract.name);
        });
    }
  }

  downloadPdf(): void {
    if (this.previewMode) {
      this.contractService.previewContract(this.templateRequest, 'pdf')
        .subscribe(blob => {
          this.contractService.downloadBlob(blob, `${this.contract.contractNumber}.pdf`);
        });
    } else {
      this.contractService.downloadContract(this.contractId, 'pdf')
        .subscribe(blob => {
          const pdfName = this.contract.name.replace(/\.(docx|doc)$/, '.pdf');
          this.contractService.downloadBlob(blob, pdfName);
        });
    }
  }

  openInNewTab(): void {
    if (this.pdfBlobUrl) {
      window.open(this.pdfBlobUrl, '_blank');
    }
  }

  zoomIn(): void {
    this.zoomLevel = Math.min(this.zoomLevel + 0.25, 3);
  }

  zoomOut(): void {
    this.zoomLevel = Math.max(this.zoomLevel - 0.25, 0.5);
  }

  resetZoom(): void {
    this.zoomLevel = 1;
  }

  getStatusClass(status: string): string {
    return 'status-' + (status || 'draft').toLowerCase().replace(/\s+/g, '-');
  }

  getPriorityClass(priority: string): string {
    return 'priority-' + (priority || 'medium').toLowerCase();
  }

  getDaysRemainingClass(): string {
    const days = this.calculateDaysRemaining();
    if (days <= 0) return 'days-critical';
    if (days <= 30) return 'days-warning';
    return 'days-normal';
  }

  calculateDaysRemaining(): number {
    if (!this.contract?.expiryDate) return 0;
    const expiry = new Date(this.contract.expiryDate);
    const today = new Date();
    const diff = expiry.getTime() - today.getTime();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  }

  isExpiringSoon(): boolean {
    return this.calculateDaysRemaining() <= 30;
  }

  formatFileSize(bytes: number): string {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }
}
